import { createClient } from '@supabase/supabase-js';

// Get environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabaseRedirectUrl = import.meta.env.VITE_SUPABASE_REDIRECT_URL;

// Validate required environment variables
if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable. Please check your .env file.');
}

if (!supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable. Please check your .env file.');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
});

function getOAuthRedirectUrl(): string | undefined {
  let url = supabaseRedirectUrl;

  if (!url && typeof window !== "undefined") {
    url = window.location.origin;
  }

  if (url) {
    // Ensure the URL ends with /auth/callback
    return url.endsWith('/auth/callback')
      ? url
      : `${url.replace(/\/$/, '')}/auth/callback`;
  }

  return undefined;
}

// Get current session with timeout
export async function getSupabaseSession() {
  try {
    // Create a timeout promise that rejects after 5 seconds
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Session check timed out')), 5000);
    });

    // Race the session check against the timeout
    const { data: { session }, error } = await Promise.race([
      supabase.auth.getSession(),
      timeoutPromise
    ]) as any;

    if (error) {
      console.error('Error getting session:', error);
      return null;
    }
    return session;
  } catch (error) {
    console.error('Session check failed or timed out:', error);
    return null;
  }
}

// Get auth token
export async function getAuthToken(): Promise<string | null> {
  const session = await getSupabaseSession();
  return session?.access_token || null;
}

// Sign in with email/password
export async function signIn(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });

  if (error) throw error;
  return data;
}

// Sign up with email/password
export async function signUp(email: string, password: string) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
  });

  if (error) throw error;
  return data;
}

// Sign out
export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// Sign in with Google OAuth
export async function signInWithGoogle() {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: "google",
    options: {
      redirectTo: getOAuthRedirectUrl(),
    },
  });

  if (error) throw error;
  return data;
}

// Check if user is authenticated
export async function isAuthenticated(): Promise<boolean> {
  const session = await getSupabaseSession();
  return !!session;
}

